import { t, tf } from "../i18n.js";
// Einstiegspunkt für kontakt.html
// Ausgelagert aus dem Inline-Script, damit import.meta.env und die
// npm-Pakete hier funktionieren.

import "../style.css";
import { supabaseClient } from "./supabase-client.js";

const FORMSPREE_URL = import.meta.env.VITE_FORMSPREE_URL;
const _supabase = supabaseClient;
let currentUser = null;

// ------------------------------------------------------------------
// TOAST (identisches Verhalten wie in ui.js, aber ohne deren Abhaengigkeiten)
// ------------------------------------------------------------------
const TOAST_COLORS = {
  success: "var(--accent-green)",
  error: "var(--accent-red)",
  info: "var(--accent-blue)",
  warning: "var(--accent-orange)",
};

function showToast(message, type = "success", duration = null) {
  const container = document.getElementById("toast-container");
  if (!container) return;

  while (container.children.length >= 4) {
    container.firstElementChild.remove();
  }

  const toast = document.createElement("div");
  toast.className = "toast toast-" + type;
  toast.style.borderLeftColor = TOAST_COLORS[type] || TOAST_COLORS.info;
  toast.setAttribute("role", type === "error" ? "alert" : "status");

  // textContent statt innerHTML: Nachrichten kommen teils aus Fehlertexten
  const label = document.createElement("span");
  label.className = "toast-text";
  label.textContent = String(message);
  toast.appendChild(label);

  toast.addEventListener("click", () => toast.remove());
  container.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add("toast-visible"));

  const ms =
    duration ||
    Math.min(6000, Math.max(2800, String(message).length * 60));
  setTimeout(() => {
    toast.classList.remove("toast-visible");
    setTimeout(() => toast.remove(), 400);
  }, ms);
}

// ------------------------------------------------------------------
// INITIALISIERUNG
// ------------------------------------------------------------------
window.addEventListener("DOMContentLoaded", async () => {
  const emailInput = document.getElementById("contact-email");
  const nameInput = document.getElementById("contact-name");
  const msgInput = document.getElementById("contact-message");
  const guestHint = document.getElementById("guest-hint");

  // Zeichenzaehler
  const counter = document.getElementById("char-count");
  msgInput.addEventListener("input", () => {
    counter.textContent = `${msgInput.value.length} / 2000`;
  });

  document
    .getElementById("contact-form")
    .addEventListener("submit", submitContactForm);

  try {
    const {
      data: { session },
    } = await _supabase.auth.getSession();

    if (session && session.user) {
      currentUser = session.user;
      // E-Mail kommt aus der Session und wird nicht editierbar angeboten
      emailInput.value = currentUser.email;
      emailInput.readOnly = true;
      emailInput.classList.add("field-readonly");

      const name =
        currentUser.user_metadata?.display_name ||
        currentUser.user_metadata?.full_name ||
        "";
      if (name) nameInput.value = name;
    } else {
      // FIX: Gaeste wurden vorher komplett ausgesperrt - inklusive
      // deaktiviertem Button ohne jeden Hinweis, wo man sich einloggt.
      guestHint.style.display = "block";
    }
  } catch (err) {
    console.error("Session-Abfrage fehlgeschlagen:", err);
    guestHint.style.display = "block";
  }
});

// ------------------------------------------------------------------
// ABSENDEN
// ------------------------------------------------------------------
async function submitContactForm(event) {
  if (event) event.preventDefault();

  const nameEl = document.getElementById("contact-name");
  const emailEl = document.getElementById("contact-email");
  const msgEl = document.getElementById("contact-message");
  const btn = document.getElementById("btn-submit");

  const name = nameEl.value.trim() || t("js.friends.021");
  // Eingeloggt: E-Mail sicher aus der Session, sonst aus dem Feld
  const email = currentUser ? currentUser.email : emailEl.value.trim();
  const category = document.getElementById("contact-category").value;
  const rawMessage = msgEl.value.trim();

  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
    emailEl.focus();
    return showToast(
      t("js.kontakt.001"),
      "warning",
    );
  }
  if (rawMessage.length < 10) {
    msgEl.focus();
    return showToast(
      t("js.kontakt.004"),
      "warning",
    );
  }

  const message = `[Kategorie: ${category}]\n\n${rawMessage}`;
  const isImportant = category === t("js.kontakt.005") || category === t("js.kontakt.006");

  btn.textContent = t("js.kontakt.007");
  btn.disabled = true;

  let storedInDb = false;
  let forwarded = false;

  try {
    // 1. In der Datenbank speichern (bei Gaesten ohne user_id)
    //
    // Die Spalte user_id fehlt in aelteren Datenbestaenden. PostgREST
    // lehnt dann den kompletten Insert ab. Deshalb: einmal mit user_id
    // versuchen, und falls die Spalte unbekannt ist, ohne sie erneut.
    // Nach der Migration (supabase/contact_messages.sql) greift direkt
    // der erste Versuch.
    if (_supabase) {
      const base = { name: name, email: email, message: message };
      const withUser = currentUser
        ? { ...base, user_id: currentUser.id }
        : base;

      let { error: sbError } = await _supabase
        .from("contact_messages")
        .insert([withUser]);

      if (sbError && /user_id/.test(sbError.message || "")) {
        console.warn(
          t("js.kontakt.002"),
        );
        ({ error: sbError } = await _supabase
          .from("contact_messages")
          .insert([base]));
      }

      if (sbError) console.error("Supabase:", sbError.message);
      else storedInDb = true;
    }

    // 2. Wichtige Anliegen zusaetzlich per E-Mail weiterleiten.
    //    Bei Gaesten immer, damit nichts verloren geht, falls die
    //    Datenbank die Zeile ablehnt.
    if (isImportant || !storedInDb) {
      const response = await fetch(FORMSPREE_URL, {
        method: "POST",
        body: JSON.stringify({ name, email, message, category }),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      forwarded = response.ok;
    }

    if (storedInDb || forwarded) {
      showToast(t("js.kontakt.008"), "success");
      msgEl.value = "";
      document.getElementById("char-count").textContent = "0 / 2000";
      document.getElementById("contact-category").value = t("js.kontakt.009");
    } else {
      showToast(
        t("js.kontakt.003"),
        "error",
      );
    }
  } catch (err) {
    console.error(err);
    showToast(
      t("js.kontakt.010") + (err.message || t("js.kontakt.011")),
      "error",
    );
  } finally {
    btn.textContent = t("js.kontakt.012");
    btn.disabled = false;
  }
}
